// Sopwith 3D - Yocton parser.
//
// Yocton is the tiny structured text format used by SDL Sopwith mission
// files (https://github.com/fragglet/yocton). An object is a sequence of
// `name: value` properties where the value is either a string or a nested
// `{ ... }` object. Strings may be bare symbols (letters, digits, `_-+.`) or
// double-quoted with C-style escapes; adjacent quoted strings can be joined
// with `&`. `//` starts a comment.
//
// This is an independent TypeScript implementation of the format.

export interface YNode {
  props: YProp[];
}

export interface YProp {
  name: string;
  value: string | YNode;
  line: number;
}

export class YoctonError extends Error {
  constructor(
    message: string,
    public readonly line: number,
  ) {
    super(`line ${line}: ${message}`);
  }
}

const enum Tok {
  EOF,
  COLON,
  OPEN,
  CLOSE,
  STRING,
}

class Lexer {
  pos = 0;
  line = 1;
  text = '';
  tokLine = 1;

  constructor(private readonly src: string) {}

  private peek(): string {
    return this.src[this.pos] ?? '';
  }

  private next(): string {
    const c = this.src[this.pos++] ?? '';
    if (c === '\n') {
      this.line++;
    }
    return c;
  }

  private skipSpaces(): boolean {
    for (;;) {
      const c = this.peek();
      if (c === '') {
        return false;
      }
      if (c === '/') {
        this.next();
        if (this.next() !== '/') {
          throw new YoctonError("expected '//' comment", this.line);
        }
        while (this.peek() !== '' && this.peek() !== '\n') {
          this.next();
        }
      } else if (c === '﻿' || /\s/.test(c)) {
        this.next();
      } else {
        return true;
      }
    }
  }

  private readEscape(): string {
    const c = this.next();
    switch (c) {
      case 'n':
        return '\n';
      case 't':
        return '\t';
      case '\\':
        return '\\';
      case '"':
        return '"';
      case 'x': {
        const hex = this.next() + this.next();
        if (!/^[0-9a-fA-F]{2}$/.test(hex)) {
          throw new YoctonError('bad \\x escape', this.line);
        }
        return String.fromCharCode(parseInt(hex, 16));
      }
      default:
        throw new YoctonError(`unknown escape '\\${c}'`, this.line);
    }
  }

  /** Reads a quoted string; the opening quote has already been consumed. */
  private readString(): Tok {
    let out = '';
    for (;;) {
      const c = this.next();
      if (c === '') {
        throw new YoctonError('unterminated string', this.line);
      }
      if (c === '"') {
        // Another chunk joined with '&'?
        if (!this.skipSpaces() || this.peek() !== '&') {
          this.text = out;
          return Tok.STRING;
        }
        this.next();
        this.tokLine = this.line;
        if (!this.skipSpaces() || this.next() !== '"') {
          throw new YoctonError("quoted string should follow '&' operator", this.line);
        }
        continue;
      }
      if (c === '\\') {
        out += this.readEscape();
      } else if (c.charCodeAt(0) < 0x20) {
        throw new YoctonError('control character inside string', this.line);
      } else {
        out += c;
      }
    }
  }

  private static isSymbolChar(c: string): boolean {
    return /[A-Za-z0-9_\-+.]/.test(c);
  }

  nextToken(): Tok {
    if (!this.skipSpaces()) {
      this.tokLine = this.line;
      return Tok.EOF;
    }
    this.tokLine = this.line;
    const c = this.next();
    switch (c) {
      case ':':
        return Tok.COLON;
      case '{':
        return Tok.OPEN;
      case '}':
        return Tok.CLOSE;
      case '"':
        return this.readString();
      case '&':
        throw new YoctonError("'&' can only join quoted strings", this.line);
      default: {
        if (!Lexer.isSymbolChar(c)) {
          throw new YoctonError(`unexpected character '${c}'`, this.line);
        }
        let s = c;
        while (this.peek() !== '' && Lexer.isSymbolChar(this.peek())) {
          s += this.next();
        }
        this.text = s;
        return Tok.STRING;
      }
    }
  }
}

function parseObject(lex: Lexer, topLevel: boolean): YNode {
  const props: YProp[] = [];
  for (;;) {
    const t = lex.nextToken();
    if (t === Tok.EOF) {
      if (!topLevel) {
        throw new YoctonError("unexpected end of file, expected '}'", lex.tokLine);
      }
      return { props };
    }
    if (t === Tok.CLOSE) {
      if (topLevel) {
        throw new YoctonError("unexpected '}'", lex.tokLine);
      }
      return { props };
    }
    if (t !== Tok.STRING) {
      throw new YoctonError('expected property name', lex.tokLine);
    }
    const name = lex.text;
    const line = lex.tokLine;
    // `name: string` or `name { ... }`.
    const sep = lex.nextToken();
    if (sep === Tok.COLON) {
      if (lex.nextToken() !== Tok.STRING) {
        throw new YoctonError(`string expected to follow ':' after '${name}'`, lex.tokLine);
      }
      props.push({ name, value: lex.text, line });
    } else if (sep === Tok.OPEN) {
      props.push({ name, value: parseObject(lex, false), line });
    } else {
      throw new YoctonError(`':' or '{' expected to follow '${name}'`, lex.tokLine);
    }
  }
}

/** Parse a whole Yocton document into a tree. Throws YoctonError on bad input. */
export function parseYocton(src: string): YNode {
  return parseObject(new Lexer(src), true);
}

/** Parse a decimal integer property value strictly, like the C parser does. */
export function yoctonInt(p: YProp): number {
  if (typeof p.value !== 'string' || !/^[-+]?\d+$/.test(p.value)) {
    throw new YoctonError(`'${p.name}' must be an integer`, p.line);
  }
  return parseInt(p.value, 10);
}

/**
 * Resolve an enum-valued property: either one of `names` or, if `tryIndex`
 * is set, a decimal index into it.
 */
export function yoctonEnum(p: YProp, names: readonly string[], tryIndex = false): number {
  if (typeof p.value !== 'string') {
    throw new YoctonError(`'${p.name}' must be a string`, p.line);
  }
  const i = names.indexOf(p.value);
  if (i >= 0) {
    return i;
  }
  if (tryIndex && /^\d+$/.test(p.value)) {
    const n = parseInt(p.value, 10);
    if (n < names.length) {
      return n;
    }
  }
  throw new YoctonError(`'${p.name}': unknown value '${p.value}'`, p.line);
}

export function yoctonObject(p: YProp): YNode {
  if (typeof p.value === 'string') {
    throw new YoctonError(`'${p.name}' must be an object`, p.line);
  }
  return p.value;
}
